import { stateAt, placeCentre } from './cityMotion';

// THE STREET'S MUSIC, TIED TO PEOPLE — which sources are playing right now,
// where, and whose music each one is (components/city/CitySounds plays them).
//
// Khari, 2026-10-29: *"I want to add more citySounds. Ambient sounds that use
// proximity and events. For example walking up to specific places (like the
// plaza) and hearing the music. This may be music from standing in front of
// the record store, or a parked car (not all of them), or perhaps an NPC
// playing their radio on the corner."* And Malaki the Menace, asked about it:
// *"Tie each source to a person. The car plays whatever a resident artist put
// on this week. The record shop plays whatever an artist last dug out of the
// crates. Then walking past isn't just hearing music, it's hearing someone's
// taste… The failure is mud. Three sources overlapping at a corner turns into
// noise… So the closest one has to win cleanly and the others duck."*
//
// A person's taste here is THEIR OWN STATION: every artist with one has a 24/7
// station of their discography plus hand-picked favourites (API
// ArtistRadioService, `artist-<id>`), on a shared clock, so everyone walking
// past hears the same bar.
//
//   THE RECORD STORE  the station of the artist in it right now — or, when
//                     nobody is, of the last one who was today (what they last
//                     dug out of the crates is still on the deck)
//   CARS              two of the kerbside cars, never all: each one a resident
//                     artist's, changing hands weekly ("what they put on this
//                     week"); never the one you are driving
//   THE COURT         a speaker: the first artist with a station to arrive and
//                     stay sets theirs down and plays it, for whoever is there
//                     (Khari, 2026-10-29: "Add this as a location, and attach a
//                     music source to it. Avatars can visit, stay and listen to
//                     what plays there. Users can visit and listen.") — and
//                     during a FREESTYLE at the court, the beat the two rappers
//                     trade verses over (a conversation with origin 'freestyle')
//
// (An NPC with a radio on a corner needs the NPC: the townsfolk are left out
// until their bodies are right. When they arrive, one is a source like these.)

// How each kind carries, in world units (a person is 0.75 tall), and how the
// player bar says where it is coming from.
export const KINDS = {
  store: { full: 2.6, far: 13, skipEnd: 3, where: (name) => `The record store · ${name}'s crate` },
  car: { full: 1.4, far: 9, skipEnd: 1.5, where: (name) => `From ${name}'s car` },
  court: { full: 2.2, far: 12, skipEnd: 1.5, where: (name) => `The court · ${name}'s speaker` },
  // The beat under two rappers: never louder than they are (peak), and it does not step back for them — it is their bed.
  freestyle: { full: 3.2, far: 15, skipEnd: 1.5, peak: 0.42, bed: true, where: (name) => `Freestyle at the court · ${name}` },
};

// Which of the kerbside cars (Vehicles finds up to six, nearest the arrival first) have the bass up.
const CAR_SLOTS = [1, 4];
const DAY = 86400000;

function isoWeek(ms) {
  const d = new Date(ms);
  const t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const day = new Date(t).getUTCDay() || 7;
  const thu = t + (4 - day) * DAY;
  const y0 = Date.UTC(new Date(thu).getUTCFullYear(), 0, 1);
  return Math.ceil(((thu - y0) / DAY + 1) / 7);
}

/**
 * The sources playing right now.
 * @param cs        the city store's state
 * @param t         the city clock (ms)
 * @param cars      driveBridge.parked ([] while the visitor is driving: they have their own radio)
 * @param driving   the index of the car the visitor is in, or null
 * @param stations  avatarId → station slug | null (who has a station of their own)
 * @returns [{ id, kind, x, z, owner: { id, name }, slug } | { …, play: { url, title, artist, art, start } }]
 */
export function soundSources(cs, t, cars, driving, stations) {
  const out = [];

  // A FREESTYLE at the court: its beat, from the court, for as long as it runs
  // (its verses are a conversation's lines, played by CityTalk). Nothing else
  // plays at the court meanwhile.
  const court = (cs.snap?.places || []).find((p) => p.slug === 'the-court');
  const session = Object.values(cs.convs || {}).find((c) => c.origin === 'freestyle' && c.beat?.url && t >= c.beat.t0 - 2000 && t < (c.beat.t1 || c.t1));
  if (session && court) {
    const [x, z] = placeCentre(court);
    const a = cs.byId[session.a]; const b = cs.byId[session.b];
    out.push({
      id: `freestyle:${session.id}`, kind: 'freestyle', x, z,
      owner: { id: session.a, name: `${a?.name || '?'} vs ${b?.name || '?'}` },
      play: { url: session.beat.url, title: session.beat.title || 'The beat', artist: session.beat.artist || '', art: session.beat.art || null, start: session.beat.t0, end: session.beat.t1 || session.t1 },
    });
  }

  const residents = Object.values(cs.byId || {})
    .filter((r) => stations.get(Number(r.avatarId)))
    .sort((a, b) => Number(a.avatarId) - Number(b.avatarId));
  if (!residents.length) return out;
  const own = (r) => ({ owner: { id: Number(r.avatarId), name: r.name }, slug: stations.get(Number(r.avatarId)) });

  let storeOwner = null;
  // The record store: whoever is in it, else whoever was last, today.
  const store = (cs.snap?.places || []).find((p) => p.slug === 'record-store');
  if (store) {
    let inside = null; let last = null; let lastEnd = -Infinity;
    for (const r of residents) {
      const st = stateAt(r, t, cs.convs);
      if (st && st.place === 'record-store' && !st.moving) { inside = r; break; }
      for (const s of r.segs || []) {
        if (s.k === 'stay' && s.p === 'record-store' && s.t1 <= t && t - s.t1 < DAY / 2 && s.t1 > lastEnd) { lastEnd = s.t1; last = r; }
      }
    }
    const who = inside || last;
    storeOwner = who;
    if (who) {
      const [x, z] = placeCentre(store);
      out.push({ id: 'store', kind: 'store', x, z, ...own(who) });
    }
  }

  // The court's speaker: whoever with a station got there first and is staying
  // (the same rule the API uses for what they remember hearing). Not during a freestyle.
  if (court && !session) {
    let holder = null; let since = Infinity; let at = null;
    for (const r of residents) {
      const st = stateAt(r, t, cs.convs);
      if (!st || st.place !== 'the-court' || st.moving) continue;
      const seg = st.seg;
      const from = seg?.t0 ?? 0;
      if (from < since) { since = from; holder = r; at = st; }
    }
    if (holder) {
      // At their feet, a step to their right.
      const h = at.heading || 0;
      const x = at.x + Math.cos(h) * 0.32; const z = at.z - Math.sin(h) * 0.32;
      out.push({ id: `court:${holder.avatarId}`, kind: 'court', x, z, y: at.y, ...own(holder) });
    }
  }

  // Two of the parked cars, each a different resident's this week — not the
  // one whose crate the record store is playing, when there are others.
  const week = isoWeek(t);
  const owners = residents.length > CAR_SLOTS.length ? residents.filter((r) => r !== storeOwner) : residents;
  CAR_SLOTS.forEach((slot, k) => {
    const c = cars?.[slot];
    if (!c || slot === driving) return;
    const r = owners[(week + k) % owners.length];
    out.push({ id: `car${slot}`, kind: 'car', x: c.x, z: c.z, ...own(r) });
  });
  return out;
}
